import { useState } from "react";
import BlogEditor from "../../layout/components/blog/BlogEditor";
import ImageUpload from "../../components/upload";
import { createBlog } from "../../api/blog";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const AddBlog = () => {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [heroImage, setHeroImage] = useState(null);
  const [content, setContent] = useState("");
  const [imageUrl, setImageURL] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const handleTitleChange = (e) => {
    const value = e.target.value;
    setTitle(value);

    const cleanSlug = value
      .toLowerCase()
      .replace(/[’']/g, "")
      .replace(/:/g, "")
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-");

      setSlug(cleanSlug);
  };

  const handleSlug = (slugStr) => {
    const cleanSlug = slugStr
      .toLowerCase()
      .replace(/[’']/g, "")
      .replace(/:/g, "")
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-");

      setSlug(cleanSlug);
  };

  const handleImageUpload = async (file) => {
    setUploading(true);
    const uploadInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(uploadInterval);
          setUploading(false);
          setImageURL(URL.createObjectURL(file));
          return 100;
        }
        return prev + 10;
      });
    }, 100);
  };

  const validateFields = () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = "Title is required";
    if (!slug.trim()) newErrors.slug = "Slug is required";
    if (!seoTitle.trim()) newErrors.seoTitle = "SEO Title is required";
    if (!seoDescription.trim())
      newErrors.seoDescription = "SEO Description is required";
    if (!content.trim()) newErrors.content = "Content is required";
    if (!imageUrl) newErrors.imageUrl = "Hero Image is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveDraft = () => {
    if (!validateFields()) return;

    const blogDraft = {
      title,
      slug,
      seoTitle,
      seoDescription,
      content,
      heroImageUrl: imageUrl,
      status: "draft",
      createdAt: new Date().toISOString(),
    };
    createBlog(blogDraft).then((resp) => {
      if (resp?.message) {
        toast.success("Blog created successfully.");
        navigate("/blogs");
      } else if (resp?.error) {
        toast.error(resp.error);
      } else {
        toast.error("Failed to save blog.");
      }
    });
  };

  const handleSubmit = () => {
    if (!validateFields()) return;

    const blogData = {
      title,
      slug,
      seoTitle,
      seoDescription,
      content,
      heroImageUrl: imageUrl,
      status: "published",
    };
    createBlog(blogData).then((resp) => {
      if (resp) {
        toast.success("Blog created successfully.");
        navigate("/blogs");
      }
    });
  };

  return (
    <div className="w-full h-full bg-white py-6">
      <div className="max-w-4xl mx-auto p-6 space-y-6 bg-white rounded-lg shadow-lg border-[0.5px] border-neutral-200">
        <h1 className="text-2xl font-semibold">Add Blog</h1>

        <div>
          <label className="block text-sm font-medium mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={handleTitleChange}
            className="w-full border border-gray-300 rounded-md py-3 px-4 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Blog Title"
          />
          {errors.title && (
            <p className="text-red-500 text-sm mt-1">{errors.title}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Slug <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={slug}
            onChange={(e) => handleSlug(e.target.value)}
            className="w-full border border-gray-300 rounded-md py-3 px-4 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="URL Slug"
          />
          {errors.slug && (
            <p className="text-red-500 text-sm mt-1">{errors.slug}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            SEO Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={seoTitle}
            onChange={(e) => setSeoTitle(e.target.value)}
            className="w-full border border-gray-300 rounded-md py-3 px-4 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="SEO Title"
          />
          {errors.seoTitle && (
            <p className="text-red-500 text-sm mt-1">{errors.seoTitle}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            SEO Description <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={seoDescription}
            onChange={(e) => setSeoDescription(e.target.value)}
            className="w-full border border-gray-300 rounded-md py-3 px-4 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="SEO Description"
          />
          {errors.seoDescription && (
            <p className="text-red-500 text-sm mt-1">{errors.seoDescription}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Upload Hero Image <span className="text-red-500">*</span>
          </label>
          <ImageUpload
            className="bg-gray-100 border border-gray-300 rounded-md p-4 text-center cursor-pointer hover:bg-gray-200 transition"
            setImageURL={setImageURL}
            setUploading={setUploading}
            setProgress={setUploadProgress}
            onFileSelect={handleImageUpload}
          >
            Click to upload image
          </ImageUpload>
          {uploading && (
            <div className="mt-2 w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-500 h-3 rounded-full transition-all duration-300 ease-in-out"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}
          {imageUrl && (
            <div className="mt-4">
              <img
                src={imageUrl}
                alt="Uploaded preview"
                className="w-full max-w-md rounded-md shadow-md"
              />
            </div>
          )}
          {errors.imageUrl && (
            <p className="text-red-500 text-sm mt-1">{errors.imageUrl}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Content <span className="text-red-500">*</span>
          </label>
          <BlogEditor value={content} setValue={setContent} readOnly={false} />
          {errors.content && (
            <p className="text-red-500 text-sm mt-1">{errors.content}</p>
          )}
        </div>

        <div className="flex justify-end space-x-4 pt-4">
          <button
            onClick={handleSaveDraft}
            className="px-5 py-2 rounded-md bg-gray-300 hover:bg-gray-400 text-black transition"
          >
            Save as Draft
          </button>
          <button
            onClick={handleSubmit}
            className="px-5 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white transition"
          >
            Publish
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddBlog;
